import { useCallback, useMemo, useState } from 'react';
import {
  Filters,
  FilterSlideout,
  SegmentedControl,
  createMultiSelectSection,
  createSingleSelectSection,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useWorkQueueContext } from '../context/useWorkQueueContext.js';
import {
  TEAM_OPTIONS,
  STUDIO_OPTIONS,
  buildDueOptions,
  buildTypeOptions,
} from '../constants/filterOptions.js';
import { SCOPE } from '../constants/mockEvents.js';

/**
 * Inbox toolbar: scope + Filters DS (search, slots, All filters, sort).
 */
function InboxFilterBar() {
  const { t } = useTranslation('work-queues-app');
  const { scope, setScope, filters, setFilter, clearFilters, sort, setSort, typeCountsByScope, loading } =
    useWorkQueueContext();
  // One stable object off the provider's memo — reading each field with `?? {}`
  // minted a fresh empty object every render and invalidated the memos below.
  const scopeStats = typeCountsByScope?.[scope];
  const typeCounts = useMemo(() => scopeStats?.typeCounts ?? {}, [scopeStats]);
  const dueCounts = useMemo(() => scopeStats?.dueCounts ?? {}, [scopeStats]);
  const customerCount = scopeStats?.customerCount ?? 0;
  const [allFiltersOpen, setAllFiltersOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  // Event Type options come from the REAL types in the queue (with counts), not
  // the mock TYPE_OPTIONS — the interim fan-out delivers change_request /
  // claim_suggestion / proposal / task, which the static list never offered.
  const typeOptions = useMemo(
    () => buildTypeOptions(typeCounts, t, { customerCount }),
    [typeCounts, customerCount, t],
  );

  // Due options come from the buckets actually present in the queue: only HITL
  // rows carry a real date, so the static DUE_OPTIONS offered Overdue/Due today/
  // This week on a governance-only queue and picking one emptied the inbox with
  // no explanation.
  const dueOptions = useMemo(
    () => buildDueOptions(dueCounts, t, { selected: filters.due }),
    [dueCounts, filters.due, t],
  );

  const openAllFilters = useCallback(() => {
    setDraft({
      team: [...filters.team],
      studio: [...filters.studio],
      type: [...filters.type],
      due: filters.due,
    });
    setAllFiltersOpen(true);
  }, [filters]);

  const closeAllFilters = useCallback(() => {
    setAllFiltersOpen(false);
    setDraft(null);
  }, []);

  const setDraftField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyAllFilters = useCallback(() => {
    if (!draft) return;
    setFilter('team', draft.team);
    setFilter('studio', draft.studio);
    setFilter('type', draft.type);
    setFilter('due', draft.due);
    closeAllFilters();
  }, [draft, setFilter, closeAllFilters]);

  const clearAllFiltersDraft = useCallback(() => {
    setDraft({ team: [], studio: [], type: [], due: null });
  }, []);

  const scopeOptions = useMemo(
    () => [
      { value: SCOPE.MY_WORK, label: t('tabs.myWork') },
      { value: SCOPE.MY_TEAM, label: t('tabs.myTeam') },
    ],
    [t],
  );

  const searchProps = useMemo(
    () => ({
      value: filters.search,
      onChange: (e) => setFilter('search', e?.target ? e.target.value : e),
      onClear: () => setFilter('search', ''),
      placeholder: t('filters.searchPlaceholder'),
    }),
    [filters.search, setFilter, t],
  );

  // Prototype bar order: Event Type · Due · Team (Studio lives in All filters).
  const filtersConfig = useMemo(
    () => [
      {
        id: 'type',
        label: t('filters.type'),
        multiple: true,
        value: filters.type,
        onChange: (next) => setFilter('type', next),
        options: typeOptions,
        // While the fan-out is still loading, typeCounts is empty and the only
        // option buildTypeOptions ever pushes unconditionally is Customer — so
        // an open dropdown would show it as if it were the sole Event Type,
        // when in reality every other type just hasn't loaded yet. Disable
        // instead of showing that misleading partial list.
        disabled: loading,
      },
      {
        id: 'due',
        label: t('filters.due'),
        multiple: false,
        value: filters.due ? [filters.due] : [],
        onChange: (next) => setFilter('due', next[0] ?? null),
        options: dueOptions,
        // Same reason as Event Type: mid-fan-out there are no buckets yet, and
        // an empty queue has none either — an open dropdown with nothing in it
        // reads as broken.
        disabled: loading || dueOptions.length === 0,
      },
      {
        id: 'team',
        label: t('filters.team'),
        multiple: true,
        value: filters.team,
        onChange: (next) => setFilter('team', next),
        options: TEAM_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
      },
      {
        id: 'studio',
        label: t('filters.studio'),
        multiple: true,
        value: filters.studio,
        onChange: (next) => setFilter('studio', next),
        options: STUDIO_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
      },
    ],
    [filters.team, filters.studio, filters.type, filters.due, setFilter, t, typeOptions, dueOptions, loading],
  );

  const sortConfig = useMemo(
    () => ({
      label: t('sort.criticality'),
      value: sort.by,
      defaultValue: 'severity',
      options: [
        { value: 'severity', label: t('sort.criticality') },
        { value: 'due_date', label: t('sort.dueDate') },
        { value: 'type', label: t('sort.type') },
      ],
      onChange: (by) => setSort((prev) => ({ ...prev, by })),
      direction: sort.direction,
      defaultDirection: 'desc',
      onDirectionChange: (direction) => setSort((prev) => ({ ...prev, direction })),
    }),
    [sort.by, sort.direction, setSort, t],
  );

  const filtersI18n = useMemo(
    () => ({
      allFilters: t('filters.allFilters'),
      clearFilters: t('filters.clearAll'),
      sortAsc: t('sort.asc'),
      sortDesc: t('sort.desc'),
    }),
    [t],
  );

  const slideoutSections = useMemo(() => {
    if (!draft) return [];
    const draftDueOptions = buildDueOptions(dueCounts, t, { selected: draft.due });
    return [
      createMultiSelectSection({
        id: 'type',
        title: t('filters.type'),
        options: typeOptions,
        value: draft.type,
        onChange: (next) => setDraftField('type', next),
      }),
      ...(draftDueOptions.length
        ? [
            createSingleSelectSection({
              id: 'due',
              title: t('filters.due'),
              options: draftDueOptions,
              value: draft.due ?? '',
              onChange: (next) => setDraftField('due', next || null),
            }),
          ]
        : []),
      createMultiSelectSection({
        id: 'team',
        title: t('filters.team'),
        options: TEAM_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        value: draft.team,
        onChange: (next) => setDraftField('team', next),
      }),
      createMultiSelectSection({
        id: 'studio',
        title: t('filters.studio'),
        options: STUDIO_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
        value: draft.studio,
        onChange: (next) => setDraftField('studio', next),
      }),
    ];
  }, [draft, setDraftField, t, typeOptions, dueCounts]);

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <SegmentedControl
        value={scope}
        onChange={setScope}
        options={scopeOptions}
        ariaLabel={t('tabs.scopeAria')}
        className="!w-auto shrink-0"
        itemClassName="!flex-none !px-3 !py-1.5"
        textClassName="text-xs"
      />

      <div className="min-w-0 flex-1">
        <Filters
          searchProps={searchProps}
          filters={filtersConfig}
          showAllFilters
          maxFilters={3}
          onAllFiltersClick={openAllFilters}
          sort={sortConfig}
          onClearAll={clearFilters}
          i18n={filtersI18n}
        />
      </div>

      <FilterSlideout
        isOpen={allFiltersOpen}
        onClose={closeAllFilters}
        title={t('filters.allFilters')}
        sections={slideoutSections}
        onClearAll={clearAllFiltersDraft}
        onApply={applyAllFilters}
        i18n={{
          title: t('filters.allFilters'),
          close: t('filters.closeAllFilters'),
          clearAll: t('filters.clearAll'),
          apply: t('filters.apply'),
        }}
      />
    </div>
  );
}

export default InboxFilterBar;
